/**
 * TagChip — Reusable descriptor tag indicator for pre-flight items.
 *
 * Displays a colored chip with icon for a descriptor tag.
 * Optionally renders a remove button for editable contexts.
 *
 * Architecture ref: Section 4.3 (Descriptor Tag Chips)
 */

import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { DescriptorTag } from '../../Data/PreFlight/preflightTypes';
import styles from './TagChip.module.css';

interface TagChipProps {
  tag: DescriptorTag;
  /** If provided, renders a remove button. */
  onRemove?: () => void;
  /** Compact mode: icon only. */
  compact?: boolean;
}

/**
 * Tag → icon + accent color class mapping.
 * Icons are Unicode emoji for now; can be replaced with Lucide in Phase 2.
 */
const TAG_CONFIG: Record<DescriptorTag, { icon: string; colorClass: string }> = {
  People:     { icon: '\uD83D\uDC65', colorClass: 'blue' },
  Expertise:  { icon: '\uD83C\uDF93', colorClass: 'purple' },
  Materials:  { icon: '\uD83D\uDCE6', colorClass: 'amber' },
  Equipment:  { icon: '\uD83D\uDD27', colorClass: 'gray' },
  Facility:   { icon: '\uD83C\uDFE2', colorClass: 'teal' },
  Funding:    { icon: '\uD83D\uDCB0', colorClass: 'green' },
  Permission: { icon: '\uD83D\uDCDD', colorClass: 'red' },
  Knowledge:  { icon: '\uD83D\uDCDA', colorClass: 'indigo' },
  Timing:     { icon: '\u23F0',        colorClass: 'orange' },
};

export default function TagChip({ tag, onRemove, compact }: TagChipProps) {
  const { t } = useAppTranslation('kpi');
  const config = TAG_CONFIG[tag];
  if (!config) return null;

  return (
    <span
      className={`${styles.chip} ${styles[config.colorClass]}`}
      title={t(`preflight.descriptorTags.${tag}`)}
    >
      <span className={styles.icon}>{config.icon}</span>
      {!compact && <span className={styles.label}>{t(`preflight.descriptorTags.${tag}`)}</span>}
      {onRemove && (
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label={`Remove ${tag}`}
        >
          &times;
        </button>
      )}
    </span>
  );
}
